/**
 * «Головна» → блок періоду: навігація, шість показників, графік місяця.
 *
 * СКЕЛЕТ за референсом замовника (презентація «Головний дашборд»): зверху
 * навігація (дата + За добу / За місяць), під нею ряд показників, нижче —
 * виконання бюджету місяця по днях із накопиченням. Оформлення — кабінетне:
 * скляні плитки .tile, токени, Urbanist, контроли 32/radius 8 (§6), догма
 * кольору (§3) — червоний лишається за реальною помилкою, тож відставання
 * від плану показане бурштином, а не ним.
 *
 * ДАНІ СПРАВЖНІ, з одного запиту: GET /owner/revenue-series?from&to віддає
 * готовий агрегат по портфелю — { totals, daily[] } з планом і фактом у
 * гривнях та МВт·год. Той самий ендпойнт уже живить графіки диспетчера й
 * власника, тобто це не новий контракт, а вже перевірений.
 *
 * ОДИН ЗАПИТ НА МІСЯЦЬ, не два. Тягнемо завжди місяць обраної дати, а режим
 * «За добу» бере рядок цього ж масиву за потрібний день. Так числа в картках
 * і стовпець у графіку ГАРАНТОВАНО з одного джерела — при двох запитах вони
 * могли б розійтися, якби бекенд перерахував дані між викликами.
 *
 * Ціна РДН приходить окремо з /market/dam-summary: це ринковий показник, не
 * портфельний, і в revenue-series його немає. Той самий запит уже робить
 * RdnPriceChips на цій же сторінці.
 */
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useEffect, useMemo, useState } from 'react';
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import { Calendar } from 'lucide-react';
import AnimatedNumber from '@/components/base/AnimatedNumber';
import { MonthField } from '@/components/base/UaCalendar';
import { API_BASE_URL, authFetch } from '@/services/api';
import { useTheme } from '@/hooks/useTheme';

/* ── дані ──────────────────────────────────────────────────────────────── */

interface DayRow {
  day: string;
  planUah: number;
  factUah: number;
  planMwh: number;
  factMwh: number;
}

interface Series {
  totals: { planUah: number; factUah: number; planMwh: number; factMwh: number };
  daily: DayRow[];
}

interface DamZone { wavg: number | null; changePct: number | null }

type Period = 'day' | 'month';

/* ── формат ────────────────────────────────────────────────────────────── */

const nf = (min: number, max: number) =>
  new Intl.NumberFormat(i18n.language === 'en' ? 'en-GB' : 'uk-UA', { minimumFractionDigits: min, maximumFractionDigits: max });

const fmtMwh = (v?: number | null) => (v == null ? '—' : nf(1, 1).format(v));

// Форматери для анімованих чисел: лічильник віддає ЧИСЛО на кожному кроці,
// тож йому потрібна функція, а не готовий рядок.
const mwh = (v: number) => nf(1, 1).format(v);
const price = (v: number) => nf(2, 2).format(v);


const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const todayIso = () => iso(new Date());

/** Межі місяця, у якому лежить дата. */
function monthRange(dateIso: string): [string, string] {
  const [y, m] = dateIso.split('-').map(Number);
  const first = new Date(y, m - 1, 1);
  const last = new Date(y, m, 0);
  return [iso(first), iso(last)];
}

const MONTHS = () => [
  i18n.t('ak_home_january_2'), i18n.t('ak_home_february_2'), i18n.t('ak_home_march_2'), i18n.t('ak_home_april_2'), i18n.t('ak_home_may_3'), i18n.t('ak_home_june_2'),
  i18n.t('ak_home_july_2'), i18n.t('ak_home_august_2'), i18n.t('ak_home_september_2'), i18n.t('ak_home_october_2'), i18n.t('ak_home_november_2'), i18n.t('ak_home_december_2'),
];
const monthName = (dateIso: string) => MONTHS()[Number(dateIso.split('-')[1]) - 1] ?? '';

/** Зсув місяця YYYY-MM на ±N. */
function shiftYm(ym: string, delta: number): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/* ── кольори графіка ───────────────────────────────────────────────────────
   Recharts не розуміє var(--…), тож токени резолвимо через getComputedStyle.
   Перерахунок прив'язаний до isDark — інакше після зміни теми графік лишився
   б у кольорах попередньої. */
function useChartColors() {
  const { isDark } = useTheme();
  return useMemo(() => {
    const css = getComputedStyle(document.documentElement);
    const v = (n: string, fb: string) => css.getPropertyValue(n).trim() || fb;
    return {
      plan: v('--series-2', '#5E656D'),
      fact: v('--ok', '#2E7D46'),
      warn: v('--warn', '#C77A1B'),
      grid: v('--line', '#E4E5E7'),
      text: v('--text', '#28323C'),
      muted: v('--text-muted', '#5E656D'),
      dim: v('--text-dim', '#9AA0A6'),
      surface: v('--surface-solid', '#FFFFFF'),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDark]);
}

/* ── відпуск і торговий графік ─────────────────────────────────────────────
   Портфельного агрегату для них немає: /owner/revenue-series віддає лише
   план і факт доходу. Відпуск за лічильником і плановий відпуск лежать у
   по-об'єктних ендпойнтах модуля «Фінансовий результат»:

     GET /financial/result/day?assetId&date     → summary.deliveredMwh / planMwh
     GET /financial/result/month?assetId&month  → totals.deliveredMwh  / planMwh

   Тому обходимо станції й сумуємо. Список беремо з
   /financial/result/stations — це САМЕ ті активи, у яких є відпуск за
   лічильником, а не весь флот: за рештою запит однаково повернув би порожньо.

   Кешуємо за ключем «період+дата»: повернення до вже переглянутої доби
   нічого не коштує. */

interface FinTotals { deliveredMwh: number | null; planMwh: number | null }

const finCache = new Map<string, FinTotals>();
let stationsCache: string[] | null = null;

async function fetchFinTotals(period: Period, date: string): Promise<FinTotals> {
  const key = `${period}:${period === 'month' ? date.slice(0, 7) : date}`;
  const hit = finCache.get(key);
  if (hit) return hit;

  if (!stationsCache) {
    const r = await authFetch(`${API_BASE_URL}/financial/result/stations`);
    const j = r.ok ? await r.json() : null;
    stationsCache = ((j?.stations ?? []) as Array<{ assetId: string }>).map((s) => s.assetId);
  }

  let delivered = 0;
  let plan = 0;
  let any = false;

  await Promise.all(stationsCache.map(async (assetId) => {
    const url = period === 'day'
      ? `${API_BASE_URL}/financial/result/day?assetId=${encodeURIComponent(assetId)}&date=${date}`
      : `${API_BASE_URL}/financial/result/month?assetId=${encodeURIComponent(assetId)}&month=${date.slice(0, 7)}`;
    try {
      const r = await authFetch(url);
      if (!r.ok) return;
      const j = await r.json();
      const t = period === 'day' ? j?.summary : j?.totals;
      if (!t) return;
      if (t.deliveredMwh != null) { delivered += Number(t.deliveredMwh); any = true; }
      if (t.planMwh != null) { plan += Number(t.planMwh); any = true; }
    } catch { /* станція без даних просто не додається до суми */ }
  }));

  // Якщо не відповів НІХТО — це не нуль, а відсутність даних. Нуль тут читався
  // б як «відпуску не було», що зовсім інше твердження.
  const out: FinTotals = any
    ? { deliveredMwh: delivered, planMwh: plan }
    : { deliveredMwh: null, planMwh: null };
  finCache.set(key, out);
  return out;
}

/* ── дрібні елементи ───────────────────────────────────────────────────── */

const CTRL: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  height: 32, borderRadius: 8, padding: '0 12px',
  background: 'transparent', border: '1px solid var(--line-strong)',
  color: 'var(--text-muted)', fontSize: 12, fontWeight: 600,
  cursor: 'pointer', whiteSpace: 'nowrap',
};

interface Metric {
  label: string;
  /**
   * Сире число — для анімації. Коли воно є, значення «набирається» до
   * потрібного, як на KPI «Прогнозу»; коли ні (даних немає), показуємо
   * готовий рядок із прочерком.
   */
  raw?: number | null;
  format?: (v: number) => string;
  value?: string;
  unit?: string;
  tone?: 'ok' | 'warn' | 'muted';
  /** Стабільний ключ показника — щоб анімація пережила перемонтування. */
  id?: string;
}

/**
 * Картка показника: один або ДВА показники в одній плитці.
 *
 * Два — бо на референсі картки парні за змістом: факт і прогноз генерації,
 * заряд і розряд BESS, розрахунковий і плановий дохід. Розбивати пару на дві
 * плитки означало б розірвати порівняння, заради якого вона й існує.
 *
 * ОБИДВА показники одного розміру (26px) і НОРМАЛЬНОЇ ваги, як числа на
 * плитках станцій: напівжирний при такому кеглі читався помітно важчим за
 * сусідні картки. У парі показники рівнозначні — факт і
 * прогноз, розрахунковий і плановий дохід. Спершу другий був дрібнішим, але
 * різний кегль у сусідніх картках робив ряд нерівним, бо десь пара, а десь
 * один показник.
 */
function Kpi({ items }: { items: Metric[] }) {
  const color = (t?: Metric['tone']) =>
    t === 'ok' ? 'var(--ok)' : t === 'warn' ? 'var(--warn)' : 'var(--text)';
  return (
    <div className="tile px-3.5 py-3" style={{ minWidth: 0, display: 'grid', gap: 8, alignContent: 'start' }}>
      {items.map((m, i) => (
        <div key={m.label} style={{ minWidth: 0 }}>
          <div className="text-[10.5px] truncate" style={{ color: 'var(--text-muted)' }}>{m.label}</div>
          <div className="flex items-baseline" style={{ gap: 4, marginTop: 2 }}>
            {m.raw != null && m.format ? (
              <AnimatedNumber
                id={m.id}
                value={m.raw}
                format={m.format}
                className="font-display tabular-nums"
                style={{
                  fontSize: 26, fontWeight: 400,
                  color: color(m.tone), lineHeight: 1.1,
                }}
              />
            ) : (
              <span className="font-display tabular-nums" style={{
                fontSize: 26, fontWeight: 400,
                color: color(m.tone), lineHeight: 1.1,
              }}>
                {m.value ?? '—'}
              </span>
            )}
            {m.unit && <span style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>{m.unit}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Грошовий показник для анімованої картки.
 *
 * Одиницю обираємо за КІНЦЕВИМ значенням і виносимо в підпис, а анімуємо вже
 * поділене число. Якби масштаб обирався всередині форматера, під час набору
 * підпис стрибав би з «тис» на «млн» просто тому, що лічильник проходить
 * через менші значення.
 */
function money(id: string, label: string, v?: number | null): Metric {
  if (v == null) return { id, label, value: '—', unit: i18n.t('ak_home_uah') };
  const big = Math.abs(v) >= 1_000_000;
  return {
    id,
    label,
    raw: big ? v / 1_000_000 : v / 1000,
    format: big ? (x: number) => nf(2, 2).format(x) : (x: number) => nf(1, 1).format(x),
    unit: big ? i18n.t('ak_home_uah_mn') : i18n.t('ak_home_uah_k'),
  };
}

/* ── гроші: найближчі 14 днів ──────────────────────────────────────────────
   Блок узятий із референсу разом із ЧИСЛАМИ: рухів по рахунку, кредитних
   платежів і графіка оплат у кабінеті немає — жоден ендпойнт такого не
   віддає, і вигадати їх з наявних даних неможливо.

   Тому цифри тут демонстраційні, рівно ті, що на слайді, і блок це прямо
   каже підписом. Показати правдоподібні, але вигадані гроші без позначки
   означало б поставити замовника в ситуацію, коли він ухвалює рішення за
   числами, яких не існує. */

interface CashEvent { date: string; title: string; amountMln: number }

const CASH_DEMO = {
  balanceMln: 3.69,
  minDate: '05.10',
  minMln: 2.91,
  events: [
    { date: '29.09', title: i18n.t('ak_home_loan_principal_250_000_interest'), amountMln: -15.6 },
    { date: '05.10', title: i18n.t('ak_home_payment_for_21_30_09'), amountMln: 4.4 },
    { date: '05.10', title: i18n.t('ak_home_payroll_social_tax_security'), amountMln: -0.18 },
    { date: '10.10', title: i18n.t('ak_home_land_lease'), amountMln: -0.09 },
    { date: '15.10', title: i18n.t('ak_home_payment_for_1_10_10'), amountMln: 6.0 },
    { date: '15.10', title: i18n.t('ak_home_o_m_management_ppko'), amountMln: -0.91 },
  ] as CashEvent[],
};

function CashPanel() {
  const d = CASH_DEMO;
  return (
    <div className="tile p-4 sm:p-5" style={{ display: 'grid', gap: 12, alignContent: 'start' }}>
      <div className="font-display" style={{
        fontSize: 12, fontWeight: 600, color: 'var(--text-muted)',
        letterSpacing: '.06em', textTransform: 'uppercase',
      }}>
        {i18n.t('ak_home_cash_next_14_days')}
      </div>

      <div>
        <div className="flex items-baseline" style={{ gap: 6 }}>
          <AnimatedNumber
            id="cash-balance"
            value={d.balanceMln}
            format={(v) => nf(2, 2).format(v)}
            className="font-display tabular-nums"
            style={{ fontSize: 26, fontWeight: 400, color: 'var(--text)', lineHeight: 1.1 }}
          />
          <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{i18n.t('ak_home_uah_mn_in_the_account')}</span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>
          {i18n.t('ak_home_minimum_over_the_period_mn_buffer_ok', { d: d.minDate, v: nf(2, 2).format(d.minMln) })}
        </div>
      </div>

      <div style={{ display: 'grid', gap: 0 }}>
        <div className="flex items-center" style={{
          gap: 10, paddingBottom: 6, borderBottom: '1px solid var(--line)',
          fontSize: 10.5, color: 'var(--text-dim)',
        }}>
          <span style={{ width: 44, flexShrink: 0 }}>{i18n.t('ak_home_date')}</span>
          <span style={{ flex: 1 }}>{i18n.t('ak_home_event')}</span>
          <span>{i18n.t('ak_home_amount')}</span>
        </div>
        {d.events.map((e, i) => (
          <div key={`${e.date}-${i}`} className="flex items-center" style={{
            gap: 10, padding: '9px 0', borderBottom: '1px solid var(--line)',
          }}>
            <span className="tabular-nums" style={{ width: 44, flexShrink: 0, fontSize: 11.5, color: 'var(--text-muted)' }}>
              {e.date}
            </span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: 'var(--text)' }}>{e.title}</span>
            {/* Надходження --ok, списання --warn. На референсі списання
                червоні, але §3 лишає червоний за реальною помилкою: планова
                оплата оренди збоєм не є. */}
            <span className="tabular-nums" style={{
              fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
              color: e.amountMln >= 0 ? 'var(--ok)' : 'var(--warn)',
            }}>
              {e.amountMln > 0 ? '+' : '−'}{nf(2, 2).format(Math.abs(e.amountMln))}
              <span style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 400 }}> {i18n.t('ak_home_mn')}</span>
            </span>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 10.5, color: 'var(--warn)' }}>
        {i18n.t('ak_home_demo_data_from_the_reference_the_cabin')}
      </div>
    </div>
  );
}

/* ── блок ──────────────────────────────────────────────────────────────── */

/**
 * `children` стають МІЖ рядом показників і графіком.
 *
 * На референсі між ними лежать мапа, плитки станцій і ціни РДН, а графік
 * доходу закриває сторінку внизу. Щоб дотримати той порядок і НЕ розривати
 * компонент на два (вони ділять одну дату й один запит), сторінка передає ці
 * блоки сюди дітьми. Розрив дав би два окремі стани, які довелось би
 * синхронізувати ззовні.
 */
export default function PeriodOverviewBlock({ children }: { children?: React.ReactNode }) {
  useTranslation();
  const c = useChartColors();
  const [period, setPeriod] = useState<Period>('day');
  const [date, setDate] = useState<string>(todayIso);
  const [data, setData] = useState<Series | null>(null);
  const [dam, setDam] = useState<DamZone | null>(null);
  const [loading, setLoading] = useState(true);

  const [from, to] = useMemo(() => monthRange(date), [date]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    authFetch(`${API_BASE_URL}/owner/revenue-series?from=${from}&to=${to}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: Series | null) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [from, to]);

  // Відпуск і торговий графік — окремо, бо збираються обходом станцій.
  const [fin, setFin] = useState<FinTotals | null>(null);
  useEffect(() => {
    let alive = true;
    setFin(null);
    fetchFinTotals(period, date)
      .then((f) => { if (alive) setFin(f); })
      .catch(() => { if (alive) setFin(null); });
    return () => { alive = false; };
  }, [period, date]);

  useEffect(() => {
    let alive = true;
    authFetch(`${API_BASE_URL}/market/dam-summary`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) setDam(d?.tiles?.day_8_23 ?? null); })
      .catch(() => { if (alive) setDam(null); });
    return () => { alive = false; };
  }, []);

  /* ── місяць графіка ──────────────────────────────────────────────────────
     Графік має власний вибір місяця, бо його питання інше, ніж у карток:
     картки відповідають «скільки за цю добу», графік — «як ішов місяць».
     Дивитися вересень у графіку, тримаючи картки на сьогоднішній добі,
     цілком осмислено.

     Але за замовчуванням він ІДЕ ЗА ДАТОЮ зверху: якщо перемкнути добу на
     інший місяць, графік перемкнеться теж — інакше він мовчки показував би
     не той період, що картки над ним. Явний вибір місяця цю прив'язку
     розриває до наступної зміни дати. */
  const [chartYm, setChartYm] = useState<string>(() => date.slice(0, 7));
  const [chartData, setChartData] = useState<Series | null>(null);
  const [chartLoading, setChartLoading] = useState(false);

  useEffect(() => { setChartYm(date.slice(0, 7)); }, [date]);

  const sameMonth = chartYm === date.slice(0, 7);

  useEffect(() => {
    if (sameMonth) { setChartData(null); setChartLoading(false); return; }
    setChartLoading(true);
    let alive = true;
    const [f, t2] = monthRange(`${chartYm}-01`);
    authFetch(`${API_BASE_URL}/owner/revenue-series?from=${f}&to=${t2}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: Series | null) => { if (alive) { setChartData(d); setChartLoading(false); } })
      .catch(() => { if (alive) { setChartData(null); setChartLoading(false); } });
    return () => { alive = false; };
  }, [chartYm, sameMonth]);

  const daily = (sameMonth ? data?.daily : chartData?.daily) ?? [];

  // Показники беруться з ОДНОГО масиву: за місяць — готові totals, за добу —
  // рядок потрібного дня. Дня може не бути в даних (майбутнє або ще не
  // порахований) — тоді картки покажуть прочерки, а не нулі: нуль означав би
  // «виробітку не було», хоча насправді немає самих даних.
  // ВАЖЛИВО: картки читають ОСНОВНИЙ ряд (місяць обраної дати), а не `daily`,
  // який може належати іншому місяцю, якщо в графіку обрали свій. Інакше при
  // розбіжності місяців добові показники мовчки зникали б.
  const row = (data?.daily ?? []).find((d) => d.day === date) ?? null;
  const totals = period === 'month' ? (data?.totals ?? null) : row;

  const factMwh = totals?.factMwh ?? null;
  const planMwh = totals?.planMwh ?? null;
  const factUah = totals?.factUah ?? null;
  const planUah = totals?.planUah ?? null;

  // Середня ціна реалізації — не окреме поле бекенда, а похідне: скільки
  // гривень принесла кожна МВт·год факту. Ділимо лише коли є на що.
  const avgPrice = factUah != null && factMwh ? factUah / factMwh : null;

  // Графік завжди місячний — як на референсі («виконання бюджету місяця»).
  // Перемикач доба/місяць керує лише картками: у режимі доби графік лишається
  // контекстом, у якому видно місце цього дня серед інших.
  const chart = useMemo(() => {
    let cf = 0;
    let cp = 0;
    return daily.map((d) => {
      cf += d.factUah ?? 0;
      cp += d.planUah ?? 0;
      return {
        dd: d.day.slice(8),
        day: d.day,
        planUah: d.planUah ?? 0,
        factUah: d.factUah ?? 0,
        cumFact: cf,
        cumPlan: cp,
      };
    });
  }, [daily]);

  const shiftDay = (delta: number) => {
    const [y, m, d] = date.split('-').map(Number);
    const dt = new Date(y, m - 1, d + delta);
    setDate(iso(dt));
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>

      {/* НАВІГАЦІЯ. Дата й перемикач періоду — контроли кабінету 32/8 з
          обводкою --line-strong, без брендового червоного (§6). */}
      <div className="flex flex-wrap items-center justify-end" style={{ gap: 8, position: 'relative' }}>
        {/* Перемикач «За добу / За місяць» — ліворуч у групі, календар із кроками
            посідає його колишнє місце (правий край): так само, як у «Цінах РДН». */}
        <div className="flex items-center" style={{ gap: 4, marginRight: 4 }}>
          {(['day', 'month'] as Period[]).map((p) => {
            const on = period === p;
            return (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                style={{
                  ...CTRL,
                  color: on ? 'var(--text)' : 'var(--text-muted)',
                  background: on ? 'color-mix(in srgb, var(--text) 6%, transparent)' : 'transparent',
                }}
              >
                {p === 'day' ? i18n.t('ak_home_per_day') : i18n.t('ak_home_per_month')}
              </button>
            );
          })}
        </div>

        <div className="flex items-center" style={{
          gap: 8, height: 32, width: 176, padding: '0 10px', borderRadius: 8, flexShrink: 0,
          background: 'var(--surface-2)', border: '1px solid var(--line)',
        }}>
          <Calendar size={14} strokeWidth={2} style={{ color: 'var(--text-dim)' }} />
          <input
            type="date"
            value={date}
            max={todayIso()}
            onChange={(e) => e.target.value && setDate(e.target.value)}
            className="outline-none cursor-pointer tabular-nums"
            style={{
              background: 'transparent', border: 'none', color: 'var(--text)',
              fontSize: 12.5, fontWeight: 600, width: '100%',
            }}
          />
        </div>

        <button type="button" onClick={() => shiftDay(-1)} style={{ ...CTRL, width: 32, padding: 0 }} title={i18n.t('ak_home_previous_day')}>‹</button>
        <button type="button" onClick={() => shiftDay(1)} style={{ ...CTRL, width: 32, padding: 0 }} title={i18n.t('ak_home_next_day')}>›</button>

        {/* Підпис періоду прибрано: він повторював те, що вже видно в самому
            полі дати й на перемикачі. Лишився тільки стан завантаження —
            його ніде більше не видно. */}
        {loading && (
          <span style={{ position: 'absolute', left: 0, whiteSpace: 'nowrap', fontSize: 11.5, color: 'var(--text-dim)' }}>
            {i18n.t('ak_home_loading')}
          </span>
        )}
      </div>

      {/* П'ЯТЬ ПАРНИХ КАРТОК — один ряд. Було шість: торговий графік і відпуск
          у мережу тримали по картці на один показник, а це план і факт того
          самого обсягу — поруч вони читаються як порівняння, так само, як
          генерація факт/прогноз. Картка BESS лишається з прочерками: дані по
          заряду й розряду будуть пізніше, і місце під них тримається тут. */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5" style={{ gap: 10 }}>
        {/* `id` у кожного показника — стабільний ключ для лічильника: без
            нього анімація не відтворилась би після перемонтування картки. */}
        <Kpi items={[
          { id: 'genFact', label: i18n.t('ak_home_generation_actual'), raw: factMwh, format: mwh, unit: i18n.t('ak_home_mwh') },
          { id: 'genPlan', label: i18n.t('ak_home_generation_forecast'), raw: planMwh, format: mwh, unit: i18n.t('ak_home_mwh') },
        ]} />
        <Kpi items={[
          { label: i18n.t('ak_home_bess_charge'), value: '—', unit: i18n.t('ak_home_mwh'), tone: 'muted' },
          { label: i18n.t('ak_home_bess_discharge'), value: '—', unit: i18n.t('ak_home_mwh'), tone: 'muted' },
        ]} />
        <Kpi items={[
          { id: 'trade', label: i18n.t('ak_home_trading_schedule'), raw: fin?.planMwh, format: mwh, unit: i18n.t('ak_home_mwh') },
          { id: 'delivered', label: i18n.t('ak_home_delivered_to_grid'), raw: fin?.deliveredMwh, format: mwh, unit: i18n.t('ak_home_mwh') },
        ]} />
        <Kpi items={[
          { id: 'avgPrice', label: i18n.t('ak_home_avg_realization_price'), raw: avgPrice, format: price, unit: i18n.t('ak_home_uah_mwh') },
          { id: 'damPrice', label: i18n.t('ak_home_dam_price_day_8_23'), raw: dam?.wavg, format: price, unit: i18n.t('ak_home_uah_mwh') },
        ]} />
        <Kpi items={[
          money('incFact', i18n.t('ak_home_income_calculated'), factUah),
          money('incPlan', i18n.t('ak_home_income_planned'), planUah),
        ]} />
      </div>

      {children}

      {/* ГРАФІК МІСЯЦЯ — останній блок сторінки, як на референсі («ДИНАМІКА
          ДОХОДУ»). Стовпці — дохід по днях (план і факт), лінії —
          накопичення. Заокруглення верхівок [3,3,0,0] і вимкнена анімація —
          як у решті графіків кабінету. */}
      {/* Вибір місяця графіка — над заголовком, по центру, як у решти навігацій
          сторінки. Заголовок «Динаміка доходу» стоїть одразу над карткою: так
          він підписує саме її, а не навігацію. */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] items-end" style={{ gap: 12, marginTop: 20 }}>
        <div className="font-display" style={{
          fontSize: 12, fontWeight: 600, color: 'var(--text-muted)',
          letterSpacing: '.06em', textTransform: 'uppercase', lineHeight: 1,
        }}>
          {i18n.t('ak_home_income_dynamics')}
        </div>
        {/* ПОРЯДОК ЯК У НАВІГАЦІЇ БЛОКУ ЗВЕРХУ: спершу поле календаря, за ним
            обидві стрілки. Раніше одна стрілка стояла перед полем — ряд читався
            інакше, ніж решта навігацій сторінки. Ширина поля стала, щоб кнопки
            не стрибали при зміні довжини підпису. */}
        <div className="flex items-center justify-end" style={{ gap: 8 }}>
          {/* Підпис місяця українською малює сам MonthField: нативний
              type=month показує назву мовою інтерфейсу браузера. */}
          <MonthField
            value={chartYm}
            max={todayIso().slice(0, 7)}
            onChange={setChartYm}
            width={176}
            title={i18n.t('ak_home_chart_month')}
            /* Та сама поверхня, що й у полі дати навігації блоку: --surface-2
               з лінією --line, а не контрастна обводка кнопок. */
            style={{
              background: 'var(--surface-2)', border: '1px solid var(--line)',
              fontSize: 12.5,
            }}
          />
          <button
            type="button"
            onClick={() => setChartYm(shiftYm(chartYm, -1))}
            style={{ ...CTRL, width: 32, padding: 0 }}
            title={i18n.t('ak_home_previous_month')}
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => setChartYm(shiftYm(chartYm, 1))}
            style={{ ...CTRL, width: 32, padding: 0 }}
            title={i18n.t('ak_home_next_month')}
          >
            ›
          </button>
          {!sameMonth && (
            <button
              type="button"
              onClick={() => setChartYm(date.slice(0, 7))}
              style={{ ...CTRL, color: 'var(--text-dim)', marginLeft: 4 }}
              title={i18n.t('ak_home_return_the_chart_to_the_month_of_the_s')}
            >
              {i18n.t('ak_home_to_selected_date')}
            </button>
          )}
        </div>
      </div>
      {/* Власного заголовка в плитці немає: над нею вже стоїть «Динаміка
          доходу», а місяць видно в навігації блоку. Два підписи поспіль про
          одне й те саме лише з'їдали висоту графіка. */}
      {/* Графік і гроші поруч: на референсі вони стоять в одному ряду,
          бо читаються разом — скільки заробили і що з цього піде далі.
          На вузьких екранах ряд розкладається вертикально. */}
      <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr]" style={{ gap: 12, alignItems: "stretch" }}>
      <div className="tile p-4 sm:p-5">

        {chart.length === 0 ? (
          <div className="flex items-center justify-center" style={{ height: 300, fontSize: 13, color: 'var(--text-dim)' }}>
            {loading || chartLoading ? i18n.t('ak_home_loading') : i18n.t('ak_home_no_data_for_this_month')}
          </div>
        ) : (
          <div style={{ height: 340 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chart} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                <XAxis dataKey="dd" tick={{ fontSize: 10, fill: c.dim }} axisLine={false} tickLine={false} interval={1} />
                <YAxis
                  yAxisId="day"
                  width={56}
                  tick={{ fontSize: 10, fill: c.dim }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v: number) => nf(0, 0).format(v / 1000)}
                  label={{ value: i18n.t('ak_home_uah_k_day'), angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: c.muted, textAnchor: 'middle' } }}
                />
                <YAxis
                  yAxisId="cum"
                  orientation="right"
                  width={56}
                  tick={{ fontSize: 10, fill: c.dim }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v: number) => nf(1, 1).format(v / 1_000_000)}
                  label={{ value: i18n.t('ak_home_uah_mn_cumulative'), angle: 90, position: 'insideRight', style: { fontSize: 10, fill: c.muted, textAnchor: 'middle' } }}
                />
                <Tooltip
                  contentStyle={{
                    background: c.surface, border: `1px solid ${c.grid}`,
                    borderRadius: 10, fontSize: 12,
                  }}
                  labelFormatter={(l) => `${l} ${monthName(chartYm + "-01")}`}
                  formatter={(v: number, n: string) => [
                    `${nf(1, 1).format(v / 1000)} ${i18n.t('ak_home_uah_k')}`,
                    n === 'planUah' ? i18n.t('ak_home_day_plan')
                      : n === 'factUah' ? i18n.t('ak_home_day_actual')
                        : n === 'cumPlan' ? i18n.t('ak_home_cumulative_plan') : i18n.t('ak_home_cumulative_actual'),
                  ]}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11, color: c.muted }}
                  formatter={(n: string) => (
                    n === 'planUah' ? i18n.t('ak_home_day_plan')
                      : n === 'factUah' ? i18n.t('ak_home_day_actual')
                        : n === 'cumPlan' ? i18n.t('ak_home_cumulative_plan') : i18n.t('ak_home_cumulative_actual')
                  )}
                />
                <Bar yAxisId="day" dataKey="planUah" fill={c.plan} fillOpacity={0.45} radius={[3, 3, 0, 0]} maxBarSize={18} isAnimationActive={false} />
                <Bar yAxisId="day" dataKey="factUah" fill={c.fact} radius={[3, 3, 0, 0]} maxBarSize={18} isAnimationActive={false} />
                <Line yAxisId="cum" type="monotone" dataKey="cumPlan" stroke={c.dim} strokeWidth={1.4} strokeDasharray="4 3" dot={false} isAnimationActive={false} />
                {/* Накопичений факт — зелений (--ok), як і стовпці факту: одна лінія
                    й одні стовпці про те саме, тож і колір спільний. */}
                <Line yAxisId="cum" type="monotone" dataKey="cumFact" stroke={c.fact} strokeWidth={2} dot={false} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      <CashPanel />
      </div>
    </div>
  );
}
